import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Search, X } from 'lucide-react';

/**
 * The per-screen topbar search. It lives in the URL as `?q=` — the screen
 * reads it like any other filter and sends it to the BE as `searchTerm` — so
 * it clears on navigation by construction (handoff: a chef query must not
 * carry into another screen). Typing is debounced; a new term returns to
 * page 1. Mount with `key={pathname}` so the box resets with the screen.
 */
export function TopbarSearch({ placeholder }: { placeholder: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const urlValue = typeof search.q === 'string' ? search.q : '';
  const [value, setValue] = useState(urlValue);
  const lastSent = useRef(urlValue);

  useEffect(() => {
    if (value === lastSent.current) return;
    const t = setTimeout(() => {
      lastSent.current = value;
      void navigate({
        to: '.',
        replace: true,
        search: ((prev: Record<string, unknown>) => ({
          ...prev,
          q: value.trim() ? value : undefined,
          page: undefined,
        })) as never,
      });
    }, 300);
    return () => clearTimeout(t);
  }, [value, navigate]);

  return (
    <div className="flex w-full max-w-[420px] items-center gap-2 rounded-full border border-[rgba(143,68,32,.12)] bg-white px-3.5 focus-within:border-orange focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-orange">
      <Search size={16} aria-hidden="true" className="shrink-0 text-muted" />
      <input
        type="search"
        aria-label={placeholder.replace(/…$/, '')}
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setValue('');
        }}
        className="min-w-0 flex-1 border-0 bg-transparent py-2 font-body text-sm text-ink outline-none placeholder:text-muted focus-visible:outline-none [&::-webkit-search-cancel-button]:hidden"
      />
      {value && (
        <button
          type="button"
          onClick={() => setValue('')}
          aria-label="Clear search"
          className="flex h-6 w-6 items-center justify-center rounded-full border-0 bg-transparent text-muted active:scale-95"
        >
          <X size={14} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
